import {
	EventDispatcher,
	MOUSE,
	Quaternion,
	Spherical,
	TOUCH,
	Vector2,
	Vector3,
    Raycaster,
    Mesh,
    PlaneGeometry,
    MeshBasicMaterial
} from 'three'
import { isM } from '../components/alpha'
import { markerMat, circleMat } from '../components/materials'

import { gsap } from "gsap"
import { Pathfinding, PathfindingHelper } from "three-pathfinding"

//

const _changeEvent = { type: 'change' }
const _startEvent = { type: 'start' }
const _endEvent = { type: 'end' }

export class FirstPersonControls {

    constructor( object, domElement ) {
        // super()

        this.enabled = true

        this.object = object // Camera
        this.target = new Vector3() // "target" sets the location of focus,

        this.domElement = domElement
        this.domElement.style.touchAction = 'none'


        // Set to false to disable rotating
		// this.enableRotate = true
		this.rotateSpeed = isM ? 0.5 : 0.25

        // How far you can orbit vertically, upper and lower limits.
		// Range is 0 to Math.PI radians.
		this.minPolarAngle = 1.25 // 0 // radians
		this.maxPolarAngle = 2 // 1.85 // Math.PI // radians

		// How far you can orbit horizontally, upper and lower limits.
		// If set, the interval [ min, max ] must be a sub-interval of [ - 2 PI, 2 PI ], with ( max - min < 2 PI )
		this.minAzimuthAngle = - Infinity // radians
		this.maxAzimuthAngle = Infinity // radians

        // Set to true to enable damping (inertia)
		// If damping is enabled, you must call controls.update() in your animation loop
		this.enableDamping = true
		this.dampingFactor = 0.075 // isM ? 0.015 : 0.05

        
        // Mouse buttons
		this.mouseButtons = { LEFT: MOUSE.ROTATE, MIDDLE: MOUSE.DOLLY, RIGHT: MOUSE.PAN }
		// Touch fingers
		this.touches = { ONE: TOUCH.ROTATE, TWO: TOUCH.DOLLY_PAN }

        //

        this.navMesh = undefined


        //
        // Public
        //

        this.update = function () {
            
            const offset = new Vector3()

            const quat = new Quaternion().setFromUnitVectors( object.up, new Vector3( 0, 1, 0 ) )
            const quatInverse = quat.clone().invert()

			const lastPosition = new Vector3()
			const lastQuaternion = new Quaternion()

			const twoPI = 2 * Math.PI

            return function update() {

                const position = scope.object.position

                offset.copy(scope.target).sub(position)
                offset.applyQuaternion(quat)
                spherical.setFromVector3( offset )

                //

                if ( scope.enableDamping ) {
					spherical.theta += sphericalDelta.theta * scope.dampingFactor;
					spherical.phi += sphericalDelta.phi * scope.dampingFactor;
				} else {
					spherical.theta += sphericalDelta.theta;
					spherical.phi += sphericalDelta.phi;
				}

                // restrict THETA to be between desired limits
				let min = scope.minAzimuthAngle;
				let max = scope.maxAzimuthAngle;
				if ( isFinite( min ) && isFinite( max ) ) {
					if ( min < - Math.PI ) min += twoPI; else if ( min > Math.PI ) min -= twoPI;
					if ( max < - Math.PI ) max += twoPI; else if ( max > Math.PI ) max -= twoPI;

					if ( min <= max ) {
						spherical.theta = Math.max( min, Math.min( max, spherical.theta ) );
					} else {
						spherical.theta = ( spherical.theta > ( min + max ) / 2 ) ?
							Math.max( min, spherical.theta ) :
							Math.min( max, spherical.theta );
					}
				}

				// restrict PHI to be between desired limits
				spherical.phi = Math.max( scope.minPolarAngle, Math.min( scope.maxPolarAngle, spherical.phi ) );

				spherical.makeSafe()

                //
                // Pan offset added in handlePan()
                //

                offset.setFromSpherical( spherical )
                offset.applyQuaternion( quatInverse )

                scope.target.copy( position ).add( offset )
                
                scope.object.lookAt( scope.target )

                //

                if ( scope.enableDamping === true ) {
					sphericalDelta.theta *= ( 1 - scope.dampingFactor )
					sphericalDelta.phi *= ( 1 - scope.dampingFactor )
				} else {
					sphericalDelta.set( 0, 0, 0 )
				}

                //

                if( lastPosition.distanceToSquared( scope.object.position ) > EPS ||
                    8 * ( 1 - lastQuaternion.dot( scope.object.quaternion ) ) > EPS ) {
                //
                    lastPosition.copy( scope.object.position )
					lastQuaternion.copy( scope.object.quaternion )
                    return true
                }

                return false
            }
        }()


        //
        // Internal
        //

        const scope = this

        const STATE = {
			NONE: - 1,
			ROTATE: 0,
			DOLLY: 1,
			PAN: 2,
			TOUCH_ROTATE: 3,
			TOUCH_PAN: 4,
			TOUCH_DOLLY_PAN: 5,
			TOUCH_DOLLY_ROTATE: 6
		}

        let state = STATE.NONE

		const EPS = 0.000001

		// current position in spherical coordinates
		const spherical = new Spherical()
		const sphericalDelta = new Spherical()

        const rotateStart = new Vector2()
		const rotateEnd = new Vector2()
		const rotateDelta = new Vector2()

        const panStart = new Vector2()
        const panEnd = new Vector2()
        const panDelta = new Vector2()

        // const pointers = []
		// const pointerPositions = {}

        //

        function rotateLeft( angle ) {
			sphericalDelta.theta += angle;
		}
		function rotateUp( angle ) {
			sphericalDelta.phi -= angle;
		}

        //

        function handlePanStart() {
            panStart.set( scope.object.position.x, scope.object.position.z )
        }
        function handlePan() {
            panEnd.set( scope.object.position.x, scope.object.position.z )
            panDelta.subVectors( panEnd, panStart )

            scope.target.add( new Vector3(panDelta.x, 0, panDelta.y) )

            panStart.copy(panEnd)
        }

        function handleMouseDownRotate( e ) {
            rotateStart.set( e.clientX, e.clientY )
        }
        function handleMouseMoveRotate( e ) {
            rotateEnd.set( e.clientX, e.clientY )

            rotateDelta.subVectors( rotateEnd, rotateStart ).multiplyScalar( scope.rotateSpeed )

            const element = scope.domElement
            rotateLeft( 2 * Math.PI * rotateDelta.x / element.clientHeight ) // yes, height
            rotateUp( 2 * Math.PI * rotateDelta.y / element.clientHeight )

            rotateStart.copy( rotateEnd )

            marker.visible = false
            pointerHandler('grabbing')

            //Call update function
            scope.update()
        }

        function handleTouchStartRotate( e ) {
            const touch = e //* Handle touch, ¿if e.target?
            rotateStart.set( touch.pageX, touch.pageY )
        }
        function handleTouchMoveRotate( e ) {
            const touch = e //* Handle touch, ¿if e.target?
            rotateEnd.set( touch.pageX, touch.pageY )

            rotateDelta.subVectors( rotateEnd, rotateStart ).multiplyScalar( scope.rotateSpeed )

			const element = scope.domElement
			rotateLeft( 2 * Math.PI * rotateDelta.x / element.clientHeight ) // yes, height
			rotateUp( 2 * Math.PI * rotateDelta.y / element.clientHeight )

			rotateStart.copy( rotateEnd )

            marker.visible = false
        }


        //
        const markerGeo = new PlaneGeometry(1, 1)
        const circle = new Mesh(markerGeo, circleMat)
        const marker = new Mesh( markerGeo, markerMat)
        marker.rotation.x = -Math.PI / 2
        marker.position.set(0, -0.1, 0)
        marker.add( circle )
        marker.ripple = circle
        marker.visible = false
        window.scene.add(marker)

        //
        const ZONE = 'level_1'
        let groupID
        let navpath
        const pathfinding = new Pathfinding()
        // const pathfindinghelper = new PathfindingHelper()
        // window.scene.add(pathfindinghelper)

        let floorIntersect
        const raycast = new Raycaster()
        let mouse = new Vector2()
        function floorRay(e) {

            if( scope.enabled === false ) return

            pointerHandler('grab')

            mouse.x = ( e.clientX / window.innerWidth ) * 2 - 1
            mouse.y = -( e.clientY / window.innerHeight ) * 2 + 1

            raycast.setFromCamera(mouse, scope.object)

            let int = raycast.intersectObject( scope.navMesh )

            if( int.length > 0 ) {
                floorIntersect = int[0].point

                marker.visible = true
                marker.position.copy(floorIntersect)
            } else {
                floorIntersect = null

                marker.visible = false
                marker.position.set(0, -0.1, 0)
            }
        }

        function markerCameraTween() {

            const t = gsap.timeline({
                onStart() {
                    handlePanStart()
                }
            })

            // Marker
            t.to({x: 0}, {
                x: 1,
                duration: 0.5,
                ease: 'power2.out',
                onUpdate() {
                    const v = this.targets()[0].x
                    marker.ripple.material.opacity = 1 - v
                    const sf = 1 + v
                    marker.ripple.scale.set(sf, sf, sf)
                }
            })

            // Camera
            pathfinding.setZoneData( ZONE, Pathfinding.createZone(scope.navMesh.geometry) )

            groupID = pathfinding.getGroup( ZONE, scope.object.position )
            const closest = pathfinding.getClosestNode( scope.object.position, ZONE, groupID )
            navpath = pathfinding.findPath( closest.centroid, floorIntersect, ZONE, groupID )
            /*if( navpath ) {
                pathfindinghelper.reset()
                pathfindinghelper.setPlayerPosition(scope.object.position)
                pathfindinghelper.setTargetPosition(floorIntersect)
                pathfindinghelper.setPath(navpath)
            }*/

            const keyframes = []
            if( navpath && navpath.length > 1 ) {
                navpath.forEach( point => keyframes.push( {x: point.x, z: point.z, ease: 'none'} ) )
            } else {
                keyframes.push( {x: floorIntersect.x, z: floorIntersect.z, ease: 'none'} )
            }

            t.to(scope.object.position, {
                keyframes: keyframes,
                duration: 1.5,
                ease: 'power1.inOut',
                onUpdate() {
                    handlePan()
                    scope.update()
                }
            })
        }

        function pointerHandler(c) {
            if(document.body.style.cursor !== c) document.body.style.cursor = c
        }



        //
        // Event handlers

        function onPointerDown( e ) {

            if( scope.enabled === false ) return

            //
            scope.domElement.addEventListener( 'pointermove', onPointerMove  )
            scope.domElement.addEventListener( 'pointerup', onPointerUp )

            //
            if( e.pointerType === 'touch') {
                onTouchStart( e )
            } else {
                onMouseDown( e )
            }
        }
        function onPointerMove( e ) {

			if ( scope.enabled === false ) return

            //
			if ( e.pointerType === 'touch' ) {
				onTouchMove( e )
			} else {
				onMouseMove( e )
			}
		}
        function onPointerUp( e ) {

            if( pointerDragged === false ) {
                if( e.pointerType === 'touch') floorRay(e)
                if( floorIntersect ) markerCameraTween()
            }

            scope.domElement.removeEventListener( 'pointermove', onPointerMove )
            scope.domElement.removeEventListener( 'pointerup', onPointerUp )

		    state = STATE.NONE

            //
            pointerDown = false
            pointerDragged = false
		}

        //
        let pointerDown = false
        let pointerDragged = false
        const pointerPosition = new Vector2()

        function onMouseDown( e ) {

            pointerDown = true
            pointerPosition.x = e.clientX
            pointerPosition.y = e.clientY

            //
            handleMouseDownRotate( e )
            state = STATE.ROTATE
        }
        function onMouseMove( e ) {

            const newPointerPosition = new Vector2( e.clientX, e.clientY )
            const tolerance = 0.5
            if( pointerDown ) {
                if( Math.abs( (pointerPosition.x - newPointerPosition.x) ) > tolerance && Math.abs( (pointerPosition.y - newPointerPosition.y) ) > tolerance ) {
                    pointerDragged = true
                } 
            }

            //
            if( state === STATE.ROTATE ) {
                handleMouseMoveRotate( e )
            }
        }

        function onTouchStart( e ) {

            pointerDown = true
            pointerPosition.x = e.clientX
            pointerPosition.y = e.clientY

            //
            handleTouchStartRotate(e)
            state = STATE.TOUCH_ROTATE
        }
        function onTouchMove( e ) {

            const newPointerPosition = new Vector2( e.clientX, e.clientY )
            const tolerance = 0.5
            if( pointerDown ) {
                if( Math.abs( (pointerPosition.x - newPointerPosition.x) ) > tolerance && Math.abs( (pointerPosition.y - newPointerPosition.y) ) > tolerance ) {
                    pointerDragged = true
                } 
            }

            //
            if( state === STATE.TOUCH_ROTATE ) {
                handleTouchMoveRotate( e )
                scope.update()
            }
        }

        //
        scope.domElement.addEventListener( 'pointerdown', onPointerDown )
        scope.domElement.addEventListener( 'pointermove', floorRay )

        //
        this.update()
    }

    splashToStart() {
        
        const helpCookie = sessionStorage.getItem('help')

        const scope = this
        const duration = helpCookie ? 2.5 : 7


        const t = gsap.timeline({
            defaults: {
                delay: 1
            },
            onComplete() {
                if( helpCookie ) scope.enabled = true
            }
        })

        t.to(scope.object.position, {
            keyframes: [
                { x: -8, y: 2.75, z: -4 }, // pos: {x: -8, y: 2.75, z: -4},
            ],
            duration,
            ease: 'power2.inOut'
        }, 0)
        t.to(scope.target, {
            keyframes: [
                { x: 0, y: 4, z: 4, ease: 'power1.out' },
                { x: 0, y: 2, z: 0, ease: 'power1.in' }, // target: {x: 0, y: 2, z: 0},
            ],
            duration,
            ease: 'power2.inOut'
        }, 0)


        if( !helpCookie ) {
            //
            t.to('.help', {
                opacity: 1,
                delay: 0.5, duration: 1,
                ease: 'power1.out',
                onStart() {
                    this.targets()[0].style.display = 'flex'
                    document.querySelector('.UI-help').classList.add('active')
                }
            })
        }

        t.to('.UI_cont', {
            opacity: 1,
            delay: 0, duration: 1,
            ease: 'power1.out',
        }, '-=0.5')
    }
}